import type { PartyExport, PartyExportPlay } from '@mixwire/contracts';

export type PlaylistTrack = {
  readonly firstPlay: PartyExportPlay;
  readonly timesPlayed: number;
};

export const spotifyTrackUrl = (trackId: string): string =>
  `https://open.spotify.com/track/${trackId}`;

// A play with no Spotify id can only be matched by what it is called, so that is its identity.
const trackKey = (play: PartyExportPlay): string =>
  play.trackId ?? JSON.stringify([play.name, play.artists, play.album]);

export const playlistTracks = (plays: readonly PartyExportPlay[]): readonly PlaylistTrack[] => {
  const keys = plays.map(trackKey);
  return plays
    .filter((_, index) => keys.indexOf(keys[index] ?? '') === index)
    .map((firstPlay) => ({
      firstPlay,
      timesPlayed: keys.filter((key) => key === trackKey(firstPlay)).length,
    }));
};

export const spotifyLinks = (plays: readonly PartyExportPlay[]): string =>
  playlistTracks(plays)
    .flatMap(({ firstPlay }) =>
      firstPlay.trackId === null ? [] : [spotifyTrackUrl(firstPlay.trackId)],
    )
    .join('\n');

const CSV_HEADER = [
  'Track URI',
  'Track Name',
  'Artist Name(s)',
  'Album Name',
  'Duration (ms)',
  'Times Played',
  'First Played At',
  'DJ',
  'Spotify URL',
] as const;

const csvField = (value: string): string =>
  /[",\r\n]/u.test(value) ? `"${value.replaceAll('"', '""')}"` : value;

const csvRow = (fields: readonly string[]): string => fields.map(csvField).join(',');

const trackRow = ({ firstPlay, timesPlayed }: PlaylistTrack): readonly string[] => [
  firstPlay.trackId === null ? '' : `spotify:track:${firstPlay.trackId}`,
  firstPlay.name,
  firstPlay.artists.join(', '),
  firstPlay.album,
  String(firstPlay.durationMs),
  String(timesPlayed),
  firstPlay.startedAt,
  firstPlay.dj ?? '',
  firstPlay.trackId === null ? '' : spotifyTrackUrl(firstPlay.trackId),
];

// The byte-order mark is what makes Excel read the file as UTF-8 rather than mangling accents.
const UTF8_BOM = '﻿';

export const playlistCsv = (plays: readonly PartyExportPlay[]): string =>
  `${UTF8_BOM}${[CSV_HEADER, ...playlistTracks(plays).map(trackRow)].map(csvRow).join('\r\n')}\r\n`;

export const exportJson = (partyExport: PartyExport): string =>
  `${JSON.stringify(partyExport, null, 2)}\n`;

export const playlistCsvFilename = (slug: string): string => `${slug}-playlist.csv`;

export const playsJsonFilename = (slug: string): string => `${slug}-plays.json`;
